/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import React, { useCallback, useState } from 'react';

import { FormattedMessage } from 'react-intl-next';

import { cssMap, jsx } from '@atlaskit/css';
import { Flex, Text } from '@atlaskit/primitives/compiled';
import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';

import messages from './messages';

const styles = cssMap({
	iframeBase: {
		width: '100%',
		height: '100%',
		border: 'none',
		transition: 'opacity 200ms ease-in-out',
	},
	iframeVisible: {
		opacity: 1,
	},
	iframeHidden: {
		opacity: 0,
	},
	loadingContainer: {
		position: 'absolute',
		top: '50%',
		left: '50%',
		transform: 'translate(-50%, -50%)',
		zIndex: 1,
	},
	errorContainer: {
		paddingTop: token('space.400'),
		paddingRight: token('space.400'),
		paddingBottom: token('space.400'),
		paddingLeft: token('space.400'),
		textAlign: 'center',
		color: token('color.text.subtle'),
	},
});

export interface PreviewIframeProps {
	spaceKey: string;
	contentId: string;
}

export const PreviewIframe = ({ spaceKey, contentId }: PreviewIframeProps) => {
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [hasError, setHasError] = useState<boolean>(false);

	const handleIframeLoad = useCallback(() => {
		setIsLoading(false);
		setHasError(false);
	}, []);

	const handleIframeError = useCallback(() => {
		setIsLoading(false);
		setHasError(true);
	}, []);

	const iframeUrl = `/wiki/spaces/${encodeURIComponent(spaceKey)}/pages/edit-smart-create/${encodeURIComponent(contentId)}`;

	if (hasError) {
		return (
			<Flex xcss={styles.errorContainer}>
				<Text>
					<FormattedMessage {...messages.errorMessage} />
				</Text>
			</Flex>
		);
	}

	return (
		<React.Fragment>
			{isLoading && (
				<Flex xcss={styles.loadingContainer}>
					<Spinner size="large" />
					<Text>
						<FormattedMessage {...messages.loadingMessage} />
					</Text>
				</Flex>
			)}
			<iframe
				src={iframeUrl}
				title={messages.iframeTitle.defaultMessage}
				css={[styles.iframeBase, isLoading ? styles.iframeHidden : styles.iframeVisible]}
				onLoad={handleIframeLoad}
				onError={handleIframeError}
				aria-label={messages.iframeTitle.defaultMessage}
			/>
		</React.Fragment>
	);
};
